import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../api/hooks';
import { usePreloaderStore, useTabsNavigationStore } from '../../../../../stores';
import { Endpoints, QueryKey, DemocracyStatus, BusinessErrorCode } from '../../../../../views/democracy/constants';
import { updateStatus } from '../../../../../views/democracy/controlsDispatcher';
import { extractErrorMessage, extractErrorCode, secondsToHHMMSS } from '../../../../../views/democracy/utils';
import GameHeader from '../GameHeader';
import GameSubTabs from '../../../topbar/GameSubTabs';
import StatusRow from '../StatusRow';
import QuestionsPanel from './questions/QuestionsPanel';
import SanctionsPanel from './sanctions/SanctionsPanel';
import ManagePanel from './manage/ManagePanel';
import BundlePanel from './BundlePanel';
import CreateDemocracyModal from './CreateDemocracyModal';
import DemocracySettingsModal from './DemocracySettingsModal';
import BundleIncompleteModal from './BundleIncompleteModal';
import '../games.css';

const DEM_STATUSES = [
    { value: DemocracyStatus.DEVELOP, label: 'Разработка', tone: 'dev' },
    { value: DemocracyStatus.WAITING_ACTIVE, label: 'Ожидание', tone: 'wait' },
    { value: DemocracyStatus.ACTIVE, label: 'Активна', tone: 'active' },
    { value: DemocracyStatus.FINISHED, label: 'Завершена', tone: 'done' }
];

// Игра «Демократия»: head-card (инфо + статусы) + под-табы + панель.
export default function DemocracyGame({ shift }) {
    const smenaId = shift.id;
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { show: showPreloader, hide: hidePreloader } = usePreloaderStore();
    const selectedGameTab = useTabsNavigationStore(state => state.selectedGameTab);

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(true);
    const [status, setStatus] = useState(null);
    const [bundleViolations, setBundleViolations] = useState(null);

    const democracyKey = QueryKey.democracy(`smena-${smenaId}`);
    const { data: democracy, isLoading, error } = useApiQuery(
        democracyKey,
        `${Endpoints.democracies.list}?smenaId=${smenaId}`,
        { refetchOnMount: 'always', enabled: !!smenaId }
    );

    const { data: full } = useApiQuery(
        QueryKey.democracyFull(democracy?.id),
        Endpoints.democracies.get(democracy?.id),
        { refetchOnMount: 'always', enabled: !!democracy?.id }
    );

    useEffect(() => { if (democracy?.status) setStatus(democracy.status); }, [democracy?.status]);

    const handleStatusChange = async (newStatus) => {
        showPreloader();
        try {
            const response = await updateStatus(democracy.id, newStatus);
            setStatus(response?.status || newStatus);
            queryClient.invalidateQueries({ queryKey: democracyKey });
            queryClient.invalidateQueries({ queryKey: QueryKey.session(democracy.id) });
            enqueueSnackbar('Статус обновлён', { variant: 'success' });
        } catch (err) {
            if (extractErrorCode(err) === BusinessErrorCode.BUNDLE_INCOMPLETE) {
                setBundleViolations(err?.response?.data?.details || []);
            } else {
                enqueueSnackbar(extractErrorMessage(err, 'Не удалось сменить статус'), { variant: 'error' });
            }
        } finally {
            hidePreloader();
        }
    };

    if (error?.response?.status === 404) {
        if (!createOpen) return <div className="vp-empty">Игра «Демократия» не создана для смены.</div>;
        return (
            <CreateDemocracyModal
                smenaId={smenaId}
                onCreated={() => queryClient.invalidateQueries({ queryKey: democracyKey })}
                onClose={() => setCreateOpen(false)}
            />
        );
    }
    if (isLoading) return <div className="vp-empty">Загрузка…</div>;
    if (!democracy) return null;

    const currentDemocracy = { ...democracy, status: status || democracy.status };
    const meta = buildMeta(full);

    return (
        <>
            <GameHeader
                title={democracy.title || `Демократия — ${shift.title || ''}`}
                meta={meta}
                onSettings={() => setSettingsOpen(true)}
                status={<StatusRow statuses={DEM_STATUSES} current={currentDemocracy.status} onChange={handleStatusChange} />}
            />
            <section className="content white">
                <GameSubTabs />
                {renderPanel(selectedGameTab, currentDemocracy)}
            </section>

            {settingsOpen && (
                <DemocracySettingsModal
                    democracy={democracy}
                    onChanged={() => queryClient.invalidateQueries({ queryKey: democracyKey })}
                    onClose={() => setSettingsOpen(false)}
                />
            )}
            {bundleViolations !== null && (
                <BundleIncompleteModal details={bundleViolations} onClose={() => setBundleViolations(null)} />
            )}
        </>
    );
}

function buildMeta(full) {
    if (!full) return null;
    const rounds = full.rounds || [];
    const questions = rounds.reduce((sum, r) => sum + (r.questions || []).length, 0);
    const budget = secondsToHHMMSS(rounds.reduce((sum, r) => sum + (r.timeBudgetSeconds || 0), 0));
    return (
        <>
            <span><b>{rounds.length}</b> раундов</span>
            <span className="dot">·</span>
            <span><b>{questions}</b> вопросов</span>
            <span className="dot">·</span>
            <span>бюджет <b>{budget}</b></span>
        </>
    );
}

function renderPanel(tab, democracy) {
    switch (tab) {
        case 'questions': return <QuestionsPanel democracy={democracy} />;
        case 'sanctions': return <SanctionsPanel democracy={democracy} />;
        case 'manage': return <ManagePanel democracy={democracy} />;
        case 'bundle': return <BundlePanel democracy={democracy} />;
        default: return <div className="vp-empty">Выберите раздел</div>;
    }
}

DemocracyGame.propTypes = {
    shift: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }).isRequired
};
